import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import moment from 'moment';
import ListingComponent, {
  MapObject,
} from 'src/components/Shared/ListingComponent';
import { WebHookSource } from 'src/redux/job-details/types';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux-hook';
import { getWebHooks } from 'src/redux/job-details/api';
import { createAbortSignal } from 'src/helper/utils';

const WebHooksList = () => {
  const dispatch = useAppDispatch();
  const webHookRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const workOrderData = useAppSelector((state) => state?.jobDetails?.data);
  const webHookList = useAppSelector(
    (state) => state?.jobDetails?.webHookDetails?.data,
  );
  const dataSource = useAppSelector((state) => state?.jobDetails?.source);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const fetchWebHook = useCallback((signal:{}) => {
    if (workOrderData?.work_order_id) {
      dispatch(getWebHooks(workOrderData?.work_order_id, signal)({}))
        .unwrap()
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.log(
            'Webhook Polling API Failed',
            {
              errors: error?.data?.errors || error?.message,
            },
          );
        });
    }
  }, [dispatch, workOrderData?.work_order_id]);

  const findDataSource = useCallback(
    (id: string) => dataSource?.[id] || {},
    [dataSource],
  );

  const renderExpandedView = useCallback(
    (webhook_log_id: string) => {
      const log = dataSource?.[webhook_log_id];
      return (
        <div>
          {log?.payload
            ? (
              <code>
                <pre>{JSON?.stringify(log?.payload, null, 2)}</pre>
              </code>
            )
            : <div className="text-center">No Response</div>}
        </div>
      );
    },
    [dataSource],
  );

  const handleRowExpand = (key: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleVisibilityChange = useCallback((signal:{}) => {
    if (document?.visibilityState === 'visible') {
      const reFetchHookDetails = () => {
        fetchWebHook(signal);
        webHookRef.current = setTimeout(reFetchHookDetails, 5000);
      };
      reFetchHookDetails();
    } else if (webHookRef.current) {
      clearTimeout(webHookRef.current);
    }
  }, [fetchWebHook]);

  useEffect(() => {
    const controller = createAbortSignal();
    handleVisibilityChange(controller?.signal);

    return () => {
      if (webHookRef.current) {
        clearTimeout(webHookRef.current);
      }
      controller?.abort();
    };
  }, [handleVisibilityChange]);

  useEffect(() => {
    const controller = createAbortSignal();

    const handleVisibilityChangeWithSignal = () => handleVisibilityChange(controller?.signal);

    document.addEventListener('visibilitychange', handleVisibilityChangeWithSignal);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChangeWithSignal);
      controller?.abort();
    };
  }, [handleVisibilityChange]);

  const mappings: Array<MapObject> = useMemo(
    () => [
      {
        key: 'expand',
        label: '',
        type: 'expand',
        className: 'is-clickable',
        handleColumnClick: (rowData: WebHookSource) => handleRowExpand(rowData?.webhook_log_id),
      },
      {
        key: 'webhook_log_id',
        label: '#',
        className: 'is-clickable',
        render: (_: WebHookSource, rowNo: number) => <div>{rowNo}</div>,
        handleColumnClick: (rowData: WebHookSource) => handleRowExpand(rowData?.webhook_log_id),
      },

      {
        key: 'created_at',
        label: 'Created At',
        className: 'is-clickable',
        render: (rowData: WebHookSource) => (
          <div>
            {moment
              .utc(rowData?.created_at)
              .format('YYYY-MM-DD HH:mm:ss [UTC]')}
          </div>
        ),
        handleColumnClick: (rowData: WebHookSource) => handleRowExpand(rowData?.webhook_log_id),
      },
      {
        key: 'eventType',
        label: 'Event Type',
        className: 'is-clickable',
        render: (rowData: WebHookSource) => (
          <div>{rowData?.payload?.eventType || 'Not Specified'}</div>
        ),
        handleColumnClick: (rowData: WebHookSource) => handleRowExpand(rowData?.webhook_log_id),
      },
      {
        key: 'status',
        label: 'Status',
        className: 'is-clickable',
        render: (rowData: WebHookSource) => {
          if (Array.isArray(rowData?.payload?.data)) {
            return (
              <div>
                {rowData?.payload?.data?.[0]?.status?.title
                 || rowData?.payload?.data?.[0]?.quote?.status?.title}
              </div>
            );
          }
          return (
            <div>
              {rowData?.payload?.data?.status?.title
               || rowData?.payload?.data?.quote?.status?.title}
            </div>
          );
        },
        handleColumnClick: (rowData: WebHookSource) => handleRowExpand(rowData?.webhook_log_id),
      },
    ],
    [],
  );

  const noData = useMemo(() => (
    <tr>
      <td colSpan={mappings?.length}>
        <div className="no-data text-center">No data found</div>
      </td>
    </tr>
  ), [mappings?.length]);

  if (!workOrderData?.work_order_id) {
    return '';
  }

  return (
    <div className="card table-container">
      <h6 className="card-subtitle">Webhook Details</h6>
      <div className="table-wrapper">
        <ListingComponent
          mappings={mappings}
          dataSource={webHookList}
          primaryKey="webhook_log_id"
          tableClassName=" table-bordered"
          findDataSource={findDataSource}
          expanded={expandedRows}
          isRowExpandable={() => false}
          expandComponent={(data) => renderExpandedView(data?.webhook_log_id)}
          customNoDataComponent={noData}
        />
      </div>
    </div>
  );
};

export default WebHooksList;
